import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import SystemDialog from "../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { ICON_SIZE } from "../../../theme/tokens";
import { useDeletePbjTrackedPlan } from "../hooks/usePbjTrackedPlan";

type Props = {
	departmentId: string;
};

/**
 * "Stop tracking" plus its confirmation. Deliberately the quietest action in the Tracker header: it
 * is rare and it deletes every tracked plan the department has saved, so it never competes with
 * "Edit plan" for the eye and always asks first.
 */
export const StopTrackingButton: FC<Props> = ({ departmentId }) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const { mutate: stopTracking, isPending } = useDeletePbjTrackedPlan();

	// The dialog stays open on failure — the snackbar says what went wrong and Confirm is the retry.
	const handleConfirm = () => stopTracking(departmentId, { onSuccess: () => setOpen(false) });
	// Not closable mid-request: closing would hide the outcome of a delete that is still in flight.
	const handleClose = () => {
		if (!isPending) setOpen(false);
	};

	return (
		<>
			<SystemButton
				buttonVariant="hairline"
				onClick={() => setOpen(true)}
				leadingIcon={<StopCircleOutlinedIcon sx={{ fontSize: ICON_SIZE.INLINE }} />}
				data-testid="pbj-tracker-stop-button"
				noFullWidthMobile
			>
				{t("pbj_model.tracker.stop.button")}
			</SystemButton>
			<SystemDialog
				open={open}
				onClose={handleClose}
				onConfirm={handleConfirm}
				title={t("pbj_model.tracker.stop.title")}
				body={t("pbj_model.tracker.stop.body")}
				confirm={t("pbj_model.tracker.stop.confirm")}
				cancel={t("pbj_model.tracker.stop.cancel")}
				confirmButtonVariant="delete"
				confirmLoading={isPending}
				maxWidth="xs"
				dataTestId="pbj-tracker-stop-dialog"
				confirmDataTestId="pbj-tracker-stop-confirm"
				cancelDataTestId="pbj-tracker-stop-cancel"
			/>
		</>
	);
};
